// RFQ action bar — the v1-style "one-click resolution" buttons that appear
// once an RFQ has been brought in and parsed. Six actions:
//
//   1. Create Quote      → createQuote() (status: 'draft'), then navigate to /quotes/:id
//   2. Create Order      → createOrder() (status: 'draft'), then navigate to /orders/:id
//   3. Rush PO           → createOrder() with rush:true (Order schema extension —
//                          a real `rush` field lands in a follow-up; for now we
//                          cast through unknown so TypeScript doesn't reject the
//                          extra key on the Firebase write)
//   4. Decline w/ feedback → modal asks reason → write /rfqs/$id/decline +
//                          draft a polite decline email to the RFQ contact
//   5. Follow up         → modal asks question → draft an email to the RFQ contact
//   6. Save for later    → no persistence; just clear the form
//
// Hard rule (project memory `feedback-drafts-never-autosend`): all outbound
// comms are saved as Outlook drafts and NEVER auto-sent. Decline + Follow-up
// both honor this.
//
// While any action is in flight every button is disabled and the active one
// shows an inline "Working…" spinner. Errors surface as a red banner above
// the bar.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, push, set } from 'firebase/database';
import { createQuote, createOrder, createPart, updateQuote, searchCustomers } from '@/shared/crm';
import { db, awaitFirebaseUser, currentFirebaseUser } from '@/shared/firebase';
import { saveToOutlookDrafts } from '@/shared/graph';
import { getActiveAccount } from '@/shared/auth';
import type { CreateOrderInput, CreatePartInput } from '@/shared/crm';
import type { PartQuotedSpec, PartType } from '@/shared/crmTypes';
import type { ExtractedRfq, ExtractedRfqPart } from '@/sdk/brain';

// Public — re-export so callers can type their `rfq` prop without importing
// from the SDK separately.
export type RfqShape = ExtractedRfq;

export interface RfqActionButtonsProps {
  /** Extracted RFQ payload from Brain.extract(). Flexible — we read defensively
   *  off the optional fields and never crash if a field is missing. */
  rfq: ExtractedRfq;
  /** Optional raw RFQ body — included in created Quote/Order notes so the
   *  human has context when picking it up. Defaults to empty string. */
  rawText?: string;
  /** Optional contact email — overrides what's parsed from the RFQ. Used to
   *  pre-fill the To: line on decline/follow-up drafts. */
  contactEmailOverride?: string;
  /** Firebase Storage URLs for files uploaded to the RFQ-pool during
   *  extraction (drawings, pictures, attachments dragged onto the panel).
   *  On Create Quote these land on `quote.attachedDrawings[]` — the pool
   *  the per-part attachedDrawings can be picked from later. Defaults
   *  to empty. */
  attachedDrawings?: string[];
  /** Client-minted ID for the in-flight RFQ (the same UUID `attachedDrawings`
   *  were stored under in /drawings/rfqs/$rfqLocalId/). Saved on
   *  `quote.fromRfqId` for trace. Optional — when not set, no fromRfqId is
   *  stamped on the new quote. */
  rfqLocalId?: string;
  /** Fired after any successful action (clears the parent form). */
  onDone?: () => void;
}

type ActionKey = 'quote' | 'order' | 'rush' | 'decline' | 'followup' | 'later';

const ACTION_LABELS: Record<ActionKey, string> = {
  quote: 'Create Quote',
  order: 'Create Order',
  rush: 'Rush PO',
  decline: 'Decline w/ feedback',
  followup: 'Follow up',
  later: 'Save for later',
};

const ACTION_ICONS: Record<ActionKey, string> = {
  quote: '📋',
  order: '📦',
  rush: '⚡',
  decline: '🚫',
  followup: '💬',
  later: '📥',
};

/** Generate a Quote/Order number from a date stamp — same heuristic as
 *  NewQuoteModal so the numbering scheme stays consistent. */
function suggestQuoteNumber(): string {
  return `Q-${Date.now().toString().slice(-5)}`;
}
function suggestOrderNumber(): string {
  return `O-${Date.now().toString().slice(-5)}`;
}

/** Compose the auto-generated part-number fallback for AI-extracted parts
 *  that didn't include one. Same shape as v1's bulk-import fallback so the
 *  numbering scheme reads consistently across origins. */
function autoPartNumber(ts: number, idx: number): string {
  return `PART-${ts.toString().slice(-6)}-${idx + 1}`;
}

/** Build a minimal CreatePartInput from an AI-extracted ExtractedRfqPart.
 *  Mirrors NewPartModal's defaults: machined type (operator picks the right
 *  type in the wizard), empty MaterialCost.perPiece, empty collection
 *  fields. Material spec from the AI lands in `notes` so the wizard can
 *  surface it — full structured material setup happens in MaterialStep. */
function buildPartInputFromExtracted(
  p: ExtractedRfqPart,
  fallbackTiers: number[],
  ts: number,
  idx: number,
): CreatePartInput {
  const number =
    p.partNum?.trim() ||
    p.customerPN?.trim() ||
    autoPartNumber(ts, idx);
  const description = (p.description?.trim() || number).slice(0, 200);
  // qtyTiers: prefer per-part breaks from AI, else fall back to the quote
  // ladder so the part has at least one tier (Part shape requires it).
  const qtyTiers =
    p.qtyBreaks && p.qtyBreaks.length > 0 ? [...p.qtyBreaks] : fallbackTiers;

  // Stash any free-form context the wizard will want — material, dimensions,
  // finish, certs, tolerances — in the notes block. Structured fields are
  // filled in during the per-type wizard.
  const noteParts: string[] = [];
  if (p.material) noteParts.push(`Material: ${p.material}`);
  if (p.dimensions) noteParts.push(`Dimensions: ${p.dimensions}`);
  if (p.finish) noteParts.push(`Finish: ${p.finish}`);
  if (p.tolerances) noteParts.push(`Tolerances: ${p.tolerances}`);
  if (p.certs) noteParts.push(`Certs: ${p.certs}`);
  if (p.notes) noteParts.push(p.notes);

  const defaultType: PartType = 'machined';

  return {
    number,
    description,
    type: defaultType,
    qtyTiers,
    materialCost: { perPiece: [] },
    machines: [],
    dxfFiles: [],
    outsideServices: [],
    tooling: [],
    attachedDrawings: [],
    notes: noteParts.length > 0 ? noteParts.join('\n') : undefined,
  };
}

/** Empty-shell PartQuotedSpec for a freshly-created Part. The cost engine
 *  needs the part to have machines + materials picked before it can
 *  compute real numbers; the quote builder lazily re-seeds these on
 *  Build / Recompute. Zeros across the board for now keeps the
 *  partIds<->partQuotedSpecs cross-check happy. */
function emptyPartQuotedSpec(qtyTiers: number[]): PartQuotedSpec {
  return {
    qtyTiers,
    estimatedSetupHrs: 0,
    estimatedRunHrs: 0,
    materialCostSnapshot: 0,
    pricePerTier: new Array<number>(qtyTiers.length).fill(0),
  };
}

/** Map RFQ → CreateQuote payload. Conservative — only fields we're confident
 *  about. `partIds` / `partQuotedSpecs` start empty; the caller pre-creates
 *  Part records (one per AI-extracted entry) and patches them in below
 *  via updateQuote. */
async function buildQuoteInputFromRfq(
  rfq: ExtractedRfq,
  rawText: string,
  attachedDrawings: string[],
  rfqLocalId: string | undefined,
) {
  const customerName = rfq.customer?.companyName?.trim() ?? '';
  let customerId = '';
  let customerNameSnapshot = customerName;

  if (customerName) {
    // Best-effort fuzzy match — searchCustomers does case-insensitive substring
    // on companyName + code. Take the first hit; if none, we leave customerId
    // empty + carry the name forward as the snapshot.
    try {
      const matches = await searchCustomers(customerName);
      if (matches.length > 0) {
        customerId = matches[0].id;
        customerNameSnapshot = matches[0].companyName;
      }
    } catch {
      // Search failure shouldn't block quote creation — keep the typed name.
    }
  }

  const notes = [
    rfq.summary ? `RFQ summary: ${rfq.summary}` : null,
    rfq.rfqRef ? `Customer RFQ ref: ${rfq.rfqRef}` : null,
    rfq.urgency ? `Urgency: ${rfq.urgency}` : null,
    rawText.trim() ? `\n--- Raw RFQ ---\n${rawText.trim()}` : null,
  ]
    .filter(Boolean)
    .join('\n');

  // qtyTiers: try to use the first part's qtyBreaks if present, else default
  // to a sensible 3-tier ladder. Quote builder can override.
  const firstPartTiers = rfq.parts?.[0]?.qtyBreaks ?? [];
  const qtyTiers = firstPartTiers.length > 0 ? firstPartTiers : [10, 50, 100];

  return {
    customerId,
    customerNameSnapshot,
    rfqRef: rfq.rfqRef,
    dueDate: rfq.dueDate,
    jobType: 'multi_part_production' as const,
    partIds: [] as string[],
    partQuotedSpecs: {},
    qtyTiers,
    status: 'draft' as const,
    imported: false,
    totalsByTier: new Array<number>(qtyTiers.length).fill(0),
    marginsByTier: new Array<number>(qtyTiers.length).fill(0),
    marginWarning: false,
    approvalRequired: false,
    approvalStatus: 'na' as const,
    revisions: [],
    followUp: {
      stage: 'paused' as const,
      draftsHistory: [],
      paused: true,
    },
    number: suggestQuoteNumber(),
    notes: notes || undefined,
    attachedDrawings: attachedDrawings.length > 0 ? attachedDrawings : undefined,
    fromRfqId: rfqLocalId,
  };
}

/** Map RFQ → CreateOrder payload. Same conservative approach. */
async function buildOrderInputFromRfq(rfq: ExtractedRfq): Promise<CreateOrderInput> {
  const customerName = rfq.customer?.companyName?.trim() ?? '';
  let customerId = '';
  let customerNameSnapshot = customerName;

  if (customerName) {
    try {
      const matches = await searchCustomers(customerName);
      if (matches.length > 0) {
        customerId = matches[0].id;
        customerNameSnapshot = matches[0].companyName;
      }
    } catch {
      // ignore — snapshot the typed name
    }
  }

  return {
    number: suggestOrderNumber(),
    customerId,
    customerNameSnapshot,
    status: 'draft',
    totalSnapshot: 0,
    poReceivedAt: new Date().toISOString(),
    promisedShipDate: rfq.dueDate,
  };
}

/** Polite default body for a decline email. Justin will edit before sending. */
function buildDeclineBody(rfq: ExtractedRfq, reason: string): { subject: string; body: string } {
  const contactName = rfq.customer?.contactName?.split(' ')[0] ?? 'there';
  const rfqRef = rfq.rfqRef ? ` (RFQ ${rfq.rfqRef})` : '';
  const subject = `RE: Your RFQ${rfqRef} — unable to quote at this time`;
  const body = `Hi ${contactName},

Thanks for thinking of Keystone Machine & Fab for this${rfqRef}.

After reviewing the requirements we're going to have to pass on this one.${reason ? `\n\nReason: ${reason}` : ''}

We appreciate the opportunity and hope to work with you on a future project that's a better fit for our shop.

Best,
Justin Jenkins
Keystone Machine & Fab
justin@keymf.com
440-865-0363`;
  return { subject, body };
}

/** Default body for a follow-up question email. */
function buildFollowUpBody(rfq: ExtractedRfq, question: string): { subject: string; body: string } {
  const contactName = rfq.customer?.contactName?.split(' ')[0] ?? 'there';
  const rfqRef = rfq.rfqRef ? ` (RFQ ${rfq.rfqRef})` : '';
  const subject = `RE: Your RFQ${rfqRef} — a quick question`;
  const body = `Hi ${contactName},

Thanks for the RFQ${rfqRef}. Before I put numbers together I have a quick question:

${question}

Once I have that I'll get back to you with a quote.

Thanks,
Justin Jenkins
Keystone Machine & Fab
justin@keymf.com
440-865-0363`;
  return { subject, body };
}

export function RfqActionButtons({
  rfq,
  rawText = '',
  contactEmailOverride,
  attachedDrawings = [],
  rfqLocalId,
  onDone,
}: RfqActionButtonsProps) {
  const navigate = useNavigate();
  const [active, setActive] = useState<ActionKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Modal state — decline & follow-up share the same modal shell with
  // different copy + a different submit handler.
  const [modal, setModal] = useState<null | 'decline' | 'followup'>(null);
  const [modalText, setModalText] = useState('');
  const [modalBusy, setModalBusy] = useState(false);

  const contactEmail =
    (contactEmailOverride ?? rfq.customer?.contactEmail ?? '').trim();

  // Pull the signed-in Outlook account once on mount so we can show a
  // disabled-with-tooltip state on decline/follow-up if the user hasn't
  // signed in with Microsoft yet (Graph drafts need an access token).
  const [outlookSignedIn, setOutlookSignedIn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      setOutlookSignedIn(getActiveAccount() !== null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  function busy(): boolean {
    return active !== null;
  }

  async function handleCreateQuote() {
    setActive('quote');
    setError(null);
    try {
      const input = await buildQuoteInputFromRfq(
        rfq,
        rawText,
        attachedDrawings,
        rfqLocalId,
      );

      // Multi-part: for each AI-extracted part, create a Part record with
      // the best-guess fields the AI gleaned. If the AI returned 0 parts,
      // fall back to the v1 behavior: empty partIds, user adds parts
      // manually in the quote builder.
      const aiParts = rfq.parts ?? [];
      const ts = Date.now();
      const createdPartIds: string[] = [];
      const partQuotedSpecs: Record<string, PartQuotedSpec> = {};

      for (let i = 0; i < aiParts.length; i++) {
        const partInput = buildPartInputFromExtracted(aiParts[i], input.qtyTiers, ts, i);
        try {
          const partId = await createPart(partInput);
          createdPartIds.push(partId);
          partQuotedSpecs[partId] = emptyPartQuotedSpec(partInput.qtyTiers);
        } catch (perPartErr) {
          // Soft-fail per part — log + continue so one bad AI suggestion
          // doesn't lose the whole quote. The estimator will see fewer
          // parts than the RFQ asked for and add the missing ones by hand.
          // eslint-disable-next-line no-console
          console.warn(
            `Failed to create Part ${i + 1} from AI extraction:`,
            perPartErr,
          );
        }
      }

      // createQuote first (empty parts) so we get a quote ID even if the
      // subsequent stitch-in fails. Then updateQuote with the created
      // partIds + their quoted-spec snapshots. Keeps the validator happy
      // (assertPartIdsSpecsConsistent runs on the merged view).
      const id = await createQuote(input, { validateFks: false });
      if (createdPartIds.length > 0) {
        await updateQuote(id, {
          partIds: createdPartIds,
          partQuotedSpecs,
        });
      }

      onDone?.();
      navigate(`/quotes/${id}`);
    } catch (e) {
      setError(`Create Quote failed: ${(e as Error).message}`);
    } finally {
      setActive(null);
    }
  }

  async function handleCreateOrder(rush: boolean) {
    setActive(rush ? 'rush' : 'order');
    setError(null);
    try {
      const input = await buildOrderInputFromRfq(rfq);
      // Schema extension: the canonical Order doesn't yet carry a `rush` flag.
      // We tack it on as an extra runtime field — Firebase will store it and
      // a follow-up migration will lift it into crmTypes.Order properly.
      const inputWithRush = rush
        ? ({ ...input, rush: true } as unknown as CreateOrderInput)
        : input;
      const id = await createOrder(inputWithRush, { validateFks: false });
      onDone?.();
      navigate(`/orders/${id}`);
    } catch (e) {
      setError(`Create Order failed: ${(e as Error).message}`);
    } finally {
      setActive(null);
    }
  }

  /** Confirm + create a rush PO. Same as createOrder but flagged. */
  async function handleRushPO() {
    if (!window.confirm('Mark as rush — bypass normal queue?')) return;
    await handleCreateOrder(true);
  }

  function openDecline() {
    setModalText('');
    setModal('decline');
  }
  function openFollowUp() {
    setModalText('');
    setModal('followup');
  }

  /** Decline-with-feedback submit. Two parts:
   *   1. Persist the decline record to /rfqs/$id/decline so we have a log
   *      of why we passed. Uses a freshly-pushed RFQ key — the RFQ intake
   *      pipeline (still being built) will eventually own these IDs;
   *      for now we just mint one inline so the record exists.
   *   2. Draft a polite decline email via saveToOutlookDrafts(). Drafts
   *      only — Justin reviews + sends from Outlook. */
  async function submitDecline() {
    setModalBusy(true);
    setError(null);
    try {
      await awaitFirebaseUser();
      // Persist the decline at /rfqs/$pushKey/decline. The intake pipeline
      // (still being built) will eventually own the parent /rfqs/$pushKey
      // record proper; for now we mint a fresh push key per decline so the
      // record exists and is queryable. push().key is non-null by contract.
      const newRfqRef = push(ref(db, 'rfqs'));
      const rfqId = newRfqRef.key!;
      const declineRecord = {
        reason: modalText.trim() || '(no reason given)',
        ts: new Date().toISOString(),
        user: currentFirebaseUser()?.uid ?? 'system',
        rfqSummary: rfq.summary,
        customerName: rfq.customer?.companyName ?? null,
        contactEmail: contactEmail || null,
      };
      await set(ref(db, `rfqs/${rfqId}/decline`), declineRecord);

      // Draft the email — only if Outlook is signed in. Otherwise we still
      // saved the decline record; just surface a friendly message.
      if (outlookSignedIn && contactEmail) {
        const { subject, body } = buildDeclineBody(rfq, modalText.trim());
        await saveToOutlookDrafts({
          to: contactEmail,
          subject,
          body,
          bodyType: 'Text',
        });
      } else if (!outlookSignedIn) {
        setError(
          'Decline logged. Sign in with Microsoft (header) to draft the email automatically.',
        );
      } else if (!contactEmail) {
        setError('Decline logged. No contact email on the RFQ — draft skipped.');
      }
      setModal(null);
      onDone?.();
    } catch (e) {
      setError(`Decline failed: ${(e as Error).message}`);
    } finally {
      setModalBusy(false);
    }
  }

  /** Follow-up question submit — drafts an email and clears the form.
   *  No Firebase write; the recent-RFQs stream will eventually surface
   *  this once we wire RFQ intake to /rfqs proper. */
  async function submitFollowUp() {
    setModalBusy(true);
    setError(null);
    try {
      if (!outlookSignedIn) {
        setError('Sign in with Microsoft (header) first to draft a follow-up email.');
        return;
      }
      if (!contactEmail) {
        setError('No contact email on the RFQ — can\'t draft a follow-up.');
        return;
      }
      const { subject, body } = buildFollowUpBody(rfq, modalText.trim());
      await saveToOutlookDrafts({
        to: contactEmail,
        subject,
        body,
        bodyType: 'Text',
      });
      setModal(null);
      onDone?.();
    } catch (e) {
      setError(`Follow-up draft failed: ${(e as Error).message}`);
    } finally {
      setModalBusy(false);
    }
  }

  function handleSaveForLater() {
    setActive('later');
    // No persistence — just clear the form.
    setTimeout(() => {
      setActive(null);
      onDone?.();
    }, 100);
  }

  return (
    <div>
      {error && (
        <div className="mb-3 bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm">
          ⚠ {error}
        </div>
      )}

      <div className="bg-paper border border-line rounded p-3">
        <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-2">
          Resolve this RFQ
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <ActionButton
            label={ACTION_LABELS.quote}
            icon={ACTION_ICONS.quote}
            tone="primary"
            disabled={busy()}
            working={active === 'quote'}
            onClick={handleCreateQuote}
          />
          <ActionButton
            label={ACTION_LABELS.order}
            icon={ACTION_ICONS.order}
            tone="primary-soft"
            disabled={busy()}
            working={active === 'order'}
            onClick={() => handleCreateOrder(false)}
          />
          <ActionButton
            label={ACTION_LABELS.rush}
            icon={ACTION_ICONS.rush}
            tone="warn"
            disabled={busy()}
            working={active === 'rush'}
            onClick={handleRushPO}
          />
          <ActionButton
            label={ACTION_LABELS.decline}
            icon={ACTION_ICONS.decline}
            tone="secondary"
            disabled={busy()}
            working={active === 'decline'}
            onClick={openDecline}
          />
          <ActionButton
            label={ACTION_LABELS.followup}
            icon={ACTION_ICONS.followup}
            tone="secondary"
            disabled={busy()}
            working={active === 'followup'}
            onClick={openFollowUp}
            title={
              !outlookSignedIn
                ? 'Sign in with Microsoft to draft a follow-up email'
                : !contactEmail
                  ? 'No contact email on this RFQ'
                  : undefined
            }
          />
          <ActionButton
            label={ACTION_LABELS.later}
            icon={ACTION_ICONS.later}
            tone="muted"
            disabled={busy()}
            working={active === 'later'}
            onClick={handleSaveForLater}
          />
        </div>
        {!outlookSignedIn && (
          <div className="mt-2 text-[11px] text-text-mute italic">
            Sign in with Microsoft (header) to enable email drafting on Decline + Follow-up.
            Quote / Order / Rush still work without it.
          </div>
        )}
      </div>

      {modal === 'decline' && (
        <ReasonModal
          title="Decline RFQ with feedback"
          icon="🚫"
          prompt="Why are you declining? (will be saved + included in the draft email)"
          placeholder="Out of capacity for this lead time, material outside our typical wheelhouse, etc."
          submitLabel="Save decline + draft email"
          value={modalText}
          onChange={setModalText}
          onSubmit={submitDecline}
          onCancel={() => setModal(null)}
          busy={modalBusy}
        />
      )}
      {modal === 'followup' && (
        <ReasonModal
          title="Follow up with a question"
          icon="💬"
          prompt="What do you need to ask the customer? (drafts only — you review + send in Outlook)"
          placeholder="Can you confirm the material spec on item 3? Is the 'or equivalent' tolerance acceptable?"
          submitLabel="Save as draft in Outlook"
          value={modalText}
          onChange={setModalText}
          onSubmit={submitFollowUp}
          onCancel={() => setModal(null)}
          busy={modalBusy}
        />
      )}
    </div>
  );
}

// =====================================================
// Internal button + modal pieces
// =====================================================

type ButtonTone = 'primary' | 'primary-soft' | 'warn' | 'secondary' | 'muted';

interface ActionButtonProps {
  label: string;
  icon: string;
  tone: ButtonTone;
  disabled: boolean;
  working: boolean;
  onClick: () => void | Promise<void>;
  title?: string;
}

function ActionButton({ label, icon, tone, disabled, working, onClick, title }: ActionButtonProps) {
  const base =
    'flex flex-col items-center justify-center text-center gap-1 px-2 py-3 rounded text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed';
  const toneClass: Record<ButtonTone, string> = {
    primary: 'bg-orange text-white hover:opacity-90',
    'primary-soft':
      'bg-paper border border-orange text-orange hover:bg-orange/10',
    warn:
      // No 'red' in the palette — amber is the closest danger tone. Bolded
      // text + amber border reads as "heads up, this is the big lever".
      'bg-amber text-white hover:opacity-90',
    secondary:
      'bg-paper border border-line-dark text-text hover:border-navy hover:text-navy',
    muted: 'bg-bg text-text-mute border border-line-dark hover:text-text',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`${base} ${toneClass[tone]}`}
    >
      <span aria-hidden className="text-base">
        {icon}
      </span>
      <span className="leading-tight">{working ? 'Working…' : label}</span>
    </button>
  );
}

interface ReasonModalProps {
  title: string;
  icon: string;
  prompt: string;
  placeholder: string;
  submitLabel: string;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void | Promise<void>;
  onCancel: () => void;
  busy: boolean;
}

function ReasonModal({
  title,
  icon,
  prompt,
  placeholder,
  submitLabel,
  value,
  onChange,
  onSubmit,
  onCancel,
  busy,
}: ReasonModalProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [busy, onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4"
      onClick={() => !busy && onCancel()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-paper rounded shadow-lg max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-line flex items-center justify-between">
          <h2 className="font-bold text-navy text-lg">
            <span aria-hidden className="mr-1">
              {icon}
            </span>
            {title}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-text-mute hover:text-navy disabled:opacity-50"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="block text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
              {prompt}
            </span>
            <textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              rows={4}
              autoFocus
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
            />
          </label>
          <p className="text-[11px] text-text-mute italic">
            Drafts only — never auto-sent. You review and send from Outlook.
          </p>
        </div>
        <div className="px-5 py-3 border-t border-line flex items-center justify-end gap-2 bg-bg/50">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={busy}
            className="px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50"
          >
            {busy ? 'Working…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
